/* SLAZY FITS — Global JavaScript */

const SlazyUtils = {
  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  },

  isTouchDevice() {
    return window.matchMedia('(pointer: coarse)').matches;
  },

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str ?? '';
    return div.innerHTML;
  },

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const format = window.SlazyFits?.moneyFormat || '${{amount}}';

    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, format);
    }

    return format
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(cents / 100).toLocaleString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
  },
};

/* ── Mobile Menu ── */
class MobileMenu {
  constructor() {
    this.toggle = document.querySelector('[data-mobile-menu-toggle]');
    this.menu = document.querySelector('[data-mobile-menu]');
    this.close = document.querySelector('[data-mobile-menu-close]');

    if (!this.toggle || !this.menu) return;

    this.toggle.addEventListener('click', () => this.open());
    this.close?.addEventListener('click', () => this.closeMenu());
    this.menu.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => this.closeMenu());
    });
  }

  open() {
    this.menu.classList.add('mobile-menu--open');
    this.menu.setAttribute('aria-hidden', 'false');
    this.toggle.setAttribute('aria-expanded', 'true');
    document.body.classList.add('is-locked');
  }

  closeMenu() {
    this.menu.classList.remove('mobile-menu--open');
    this.menu.setAttribute('aria-hidden', 'true');
    this.toggle.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('is-locked');
  }
}

/* ── Sticky Header (fixed + glass on scroll) ── */
class StickyHeader {
  constructor() {
    this.wrapper = document.querySelector('[data-header-wrapper]');
    this.header = document.querySelector('[data-header]') || document.querySelector('.header');
    this.bars = document.querySelector('[data-header-bars]');
    this.spacer = document.querySelector('[data-header-spacer]');

    if (!this.wrapper || !this.header) return;

    let ticking = false;

    this.update = () => {
      const scrollY = window.scrollY || window.pageYOffset;
      const barsHeight = this.bars ? this.bars.offsetHeight : 0;
      const wrapperHeight = this.wrapper.offsetHeight;
      const fixThreshold = Math.max(barsHeight - 4, 8);
      const isFixed = scrollY > fixThreshold;
      const isGlass = scrollY > 16;

      this.wrapper.classList.toggle('header-wrapper--fixed', isFixed);
      this.header.classList.toggle('header--scrolled', isGlass);

      if (this.spacer) {
        this.spacer.style.height = isFixed ? `${wrapperHeight}px` : '0px';
      }

      document.documentElement.style.setProperty('--header-fixed-height', `${wrapperHeight}px`);
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        this.update();
        ticking = false;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    window.addEventListener('orientationchange', onScroll, { passive: true });

    if (document.fonts?.ready) {
      document.fonts.ready.then(onScroll);
    }

    onScroll();
  }
}

/* ── Page Loader ── */
class PageLoader {
  constructor() {
    this.loader = document.querySelector('[data-page-loader]');
    if (!this.loader) return;

    document.body.classList.add('is-loading');

    const finish = () => {
      this.loader.classList.add('is-done');
      document.body.classList.remove('is-loading');
      document.dispatchEvent(new CustomEvent('slazy:loaded'));
    };

    if (document.readyState === 'complete') {
      setTimeout(finish, 500);
    } else {
      window.addEventListener('load', () => setTimeout(finish, 500), { once: true });
    }
  }
}

/* ── Hero Entrance + Parallax ── */
class HeroMotion {
  constructor() {
    this.heroes = document.querySelectorAll('[data-hero-entrance]');
    if (!this.heroes.length) return;

    this.heroes.forEach((hero) => {
      requestAnimationFrame(() => hero.classList.add('is-entered'));
    });

    if (SlazyUtils.prefersReducedMotion() || SlazyUtils.isTouchDevice()) return;

    this.heroes.forEach((hero) => {
      hero.addEventListener('mousemove', (e) => this.onMove(e, hero));
      hero.addEventListener('mouseleave', () => this.onLeave(hero));
    });
  }

  onMove(e, hero) {
    const rect = hero.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    const media = hero.querySelector('[data-hero-media] img, [data-hero-media] video');

    if (media) {
      media.style.transform = `scale(1.04) translate(${x * 14}px, ${y * 8}px)`;
    }
  }

  onLeave(hero) {
    const media = hero.querySelector('[data-hero-media] img, [data-hero-media] video');
    if (media) media.style.transform = '';
  }
}

/* ── Scroll Reveal ── */
class ScrollReveal {
  constructor() {
    const elements = document.querySelectorAll('[data-animate], [data-animate-stagger]');
    if (!elements.length) return;

    if (SlazyUtils.prefersReducedMotion()) {
      elements.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -5% 0px' }
    );

    elements.forEach((el) => observer.observe(el));
  }
}

/* ── Parallax Banners ── */
class ParallaxBanners {
  constructor() {
    if (SlazyUtils.prefersReducedMotion() || SlazyUtils.isTouchDevice()) return;

    this.banners = document.querySelectorAll('[data-parallax]');
    if (!this.banners.length) return;

    this.onScroll = () => this.update();
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.update();
  }

  update() {
    this.banners.forEach((banner) => {
      const img = banner.querySelector('img');
      if (!img) return;

      const rect = banner.getBoundingClientRect();
      const center = rect.top + rect.height / 2;
      const offset = (center - window.innerHeight / 2) * 0.03;
      img.style.transform = `translateY(${offset}px) scale(1.03)`;
    });
  }
}

/* ── Info Bar Rotator ── */
class InfoBarRotator {
  constructor(element) {
    this.items = element.querySelectorAll('.info-bar__rotator-item');
    if (this.items.length <= 1) return;

    this.current = 0;
    this.interval = setInterval(() => this.next(), 3500);
  }

  next() {
    this.items[this.current].classList.remove('info-bar__rotator-item--active');
    this.current = (this.current + 1) % this.items.length;
    this.items[this.current].classList.add('info-bar__rotator-item--active');
  }
}

/* ── Product Page Gallery + Lightbox ── */
let productPageGalleryInstance = null;

class ProductPageGallery {
  constructor() {
    this.page = document.querySelector('[data-product-page]');
    if (!this.page) return;

    productPageGalleryInstance = this;

    this.mainImage = this.page.querySelector('[data-product-main-image]');
    this.mainOpenBtn = this.page.querySelector('[data-product-lightbox-open]');
    this.thumbs = [...this.page.querySelectorAll('[data-product-thumb]')];
    this.lightbox = document.querySelector('[data-product-lightbox]');

    const jsonEl = document.querySelector('[data-product-media-json]');
    if (jsonEl) {
      try {
        this.media = JSON.parse(jsonEl.textContent);
      } catch (e) {
        this.media = [];
      }
    } else {
      this.media = [];
    }

    this.index = Math.max(0, this.thumbs.findIndex((t) => t.classList.contains('product-page__thumb--active')));
    if (this.index < 0) this.index = 0;

    this.isOpen = false;
    this.lastFocusEl = null;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.boundKeydown = this.onKeydown.bind(this);

    if (this.mainImage) {
      this.mainImage.style.transition = 'opacity 0.25s ease';
    }

    this.mainOpenBtn?.addEventListener('click', () => this.openLightbox(this.index));

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const idx = Number(thumb.dataset.index);
        if (Number.isNaN(idx)) return;
        this.openLightbox(idx);
      });
    });

    document.querySelectorAll('[data-showcase-media-open]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = Number(btn.dataset.mediaIndex);
        if (Number.isNaN(idx)) return;
        this.openLightbox(idx, btn);
      });
    });

    if (!this.lightbox || this.media.length === 0) return;

    this.lightboxImage = this.lightbox.querySelector('[data-lightbox-image]');
    this.lightboxVideoWrap = this.lightbox.querySelector('[data-lightbox-video-wrap]');
    this.lightboxVideo = this.lightbox.querySelector('[data-lightbox-video]');
    this.lightboxEmbed = this.lightbox.querySelector('[data-lightbox-embed]');
    this.lightboxCounter = this.lightbox.querySelector('[data-lightbox-counter]');
    this.lightboxStage = this.lightbox.querySelector('[data-lightbox-stage]');
    this.prevBtn = this.lightbox.querySelector('[data-lightbox-prev]');
    this.nextBtn = this.lightbox.querySelector('[data-lightbox-next]');

    this.lightbox.querySelectorAll('[data-lightbox-close]').forEach((el) => {
      el.addEventListener('click', () => this.closeLightbox());
    });

    this.prevBtn?.addEventListener('click', () => this.step(-1));
    this.nextBtn?.addEventListener('click', () => this.step(1));

    this.lightboxStage?.addEventListener('touchstart', (e) => this.onTouchStart(e), { passive: true });
    this.lightboxStage?.addEventListener('touchend', (e) => this.onTouchEnd(e), { passive: true });
  }

  static getInstance() {
    return productPageGalleryInstance;
  }

  setIndex(index, { updateMain = true } = {}) {
    if (!this.media.length) return;
    const next = ((index % this.media.length) + this.media.length) % this.media.length;
    this.index = next;

    const item = this.media[next];
    if (!item) return;

    this.thumbs.forEach((thumb, i) => {
      const active = i === next;
      thumb.classList.toggle('product-page__thumb--active', active);
      thumb.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    if (updateMain && this.mainImage) {
      this.mainImage.style.opacity = '0';
      window.setTimeout(() => {
        this.mainImage.src = item.thumb || item.src;
        this.mainImage.alt = item.alt || '';
        this.mainImage.style.opacity = '1';
      }, 150);
    }

    if (this.isOpen) {
      this.renderLightboxImage();
    }
  }

  setIndexByImageId(imageId) {
    if (!imageId || !this.media.length) return;
    const idx = this.media.findIndex((item) => String(item.id) === String(imageId));
    if (idx >= 0) this.setIndex(idx);
  }

  setIndexBySrc(src) {
    if (!src || !this.media.length) return;
    const normalized = src.split('?')[0];
    const idx = this.media.findIndex((item) => {
      const thumb = (item.thumb || '').split('?')[0];
      const full = (item.src || '').split('?')[0];
      return normalized.includes(thumb) || thumb.includes(normalized) || normalized.includes(full) || full.includes(normalized);
    });
    if (idx >= 0) this.setIndex(idx);
  }

  openLightbox(index = this.index, triggerEl = null) {
    if (!this.lightbox || !this.media.length) return;

    this.lastFocusEl = triggerEl || this.mainOpenBtn || document.activeElement;
    this.setIndex(index);
    this.isOpen = true;
    this.lightbox.hidden = false;
    this.lightbox.setAttribute('aria-hidden', 'false');
    document.body.classList.add('is-locked');
    document.addEventListener('keydown', this.boundKeydown);

    this.renderLightboxImage();
    this.lightbox.querySelector('[data-lightbox-close]')?.focus();
  }

  closeLightbox() {
    if (!this.lightbox || !this.isOpen) return;

    this.pauseLightboxMedia();
    this.isOpen = false;
    this.lightbox.hidden = true;
    this.lightbox.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('is-locked');
    document.removeEventListener('keydown', this.boundKeydown);
    if (this.lastFocusEl && typeof this.lastFocusEl.focus === 'function') {
      this.lastFocusEl.focus();
    }
  }

  step(delta) {
    if (this.media.length <= 1) return;
    this.setIndex(this.index + delta);
  }

  renderLightboxImage() {
    const item = this.media[this.index];
    if (!item) return;

    this.pauseLightboxMedia();

    const isVideo = item.type === 'video' || item.type === 'external_video';

    if (isVideo) {
      if (this.lightboxImage) this.lightboxImage.hidden = true;
      if (this.lightboxVideoWrap) this.lightboxVideoWrap.hidden = false;

      if (item.type === 'video' && this.lightboxVideo) {
        if (this.lightboxEmbed) {
          this.lightboxEmbed.hidden = true;
          this.lightboxEmbed.innerHTML = '';
        }
        this.lightboxVideo.hidden = false;
        this.lightboxVideo.innerHTML = '';
        (item.sources || []).forEach((source) => {
          const sourceEl = document.createElement('source');
          sourceEl.src = source.url;
          if (source.mimeType) sourceEl.type = source.mimeType;
          this.lightboxVideo.appendChild(sourceEl);
        });
        this.lightboxVideo.load();
        this.lightboxVideo.classList.remove('is-loaded');
        this.lightboxVideo.onloadeddata = () => {
          this.lightboxVideo.classList.add('is-loaded');
        };
      } else if (item.type === 'external_video' && this.lightboxEmbed) {
        if (this.lightboxVideo) {
          this.lightboxVideo.hidden = true;
          this.lightboxVideo.pause();
        }
        this.lightboxEmbed.hidden = false;
        let embedUrl = '';
        if (item.host === 'youtube') {
          embedUrl = `https://www.youtube.com/embed/${item.externalId}?autoplay=1&rel=0`;
        } else if (item.host === 'vimeo') {
          embedUrl = `https://player.vimeo.com/video/${item.externalId}?autoplay=1`;
        }
        this.lightboxEmbed.innerHTML = embedUrl
          ? `<iframe src="${embedUrl}" title="${item.alt || 'Product video'}" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen loading="lazy"></iframe>`
          : '';
      }
    } else if (this.lightboxImage) {
      if (this.lightboxVideoWrap) this.lightboxVideoWrap.hidden = true;
      this.lightboxImage.hidden = false;
      this.lightboxImage.classList.remove('is-loaded');
      this.lightboxImage.src = item.src;
      this.lightboxImage.alt = item.alt || '';

      if (this.lightboxImage.complete) {
        this.lightboxImage.classList.add('is-loaded');
      } else {
        this.lightboxImage.onload = () => {
          this.lightboxImage.classList.add('is-loaded');
        };
      }
    }

    if (this.lightboxCounter) {
      this.lightboxCounter.textContent = `${this.index + 1} / ${this.media.length}`;
    }

    const single = this.media.length <= 1;
    if (this.prevBtn) this.prevBtn.disabled = single;
    if (this.nextBtn) this.nextBtn.disabled = single;
  }

  pauseLightboxMedia() {
    if (this.lightboxVideo) {
      this.lightboxVideo.pause();
      this.lightboxVideo.innerHTML = '';
    }
    if (this.lightboxEmbed) {
      this.lightboxEmbed.innerHTML = '';
      this.lightboxEmbed.hidden = true;
    }
  }

  onKeydown(event) {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeLightbox();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.step(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.step(1);
    }
  }

  onTouchStart(event) {
    const touch = event.changedTouches[0];
    this.touchStartX = touch.screenX;
    this.touchStartY = touch.screenY;
  }

  onTouchEnd(event) {
    const touch = event.changedTouches[0];
    const deltaX = touch.screenX - this.touchStartX;
    const deltaY = touch.screenY - this.touchStartY;

    if (Math.abs(deltaX) < 50 || Math.abs(deltaX) < Math.abs(deltaY)) return;

    if (deltaX > 0) {
      this.step(-1);
    } else {
      this.step(1);
    }
  }
}

/* ── Product Media Showcase (scroll to form) ── */
class ProductMediaShowcase {
  constructor() {
    document.querySelectorAll('[data-pms-scroll-to-form]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const target =
          document.querySelector('.product-page__info') ||
          document.querySelector('#ProductPurchaseForm') ||
          document.querySelector('[data-product-form]');
        if (!target) return;

        target.scrollIntoView({ behavior: 'smooth', block: 'start' });

        window.setTimeout(() => {
          const addBtn = document.querySelector('[data-add-to-cart]');
          if (addBtn && !addBtn.disabled) {
            addBtn.focus({ preventScroll: true });
          }
        }, 500);
      });
    });
  }
}

/* ── Quantity Selector ── */
class QuantitySelector {
  constructor() {
    document.querySelectorAll('[data-quantity-selector]').forEach((selector) => {
      const minus = selector.querySelector('[data-qty-minus]');
      const plus = selector.querySelector('[data-qty-plus]');
      const input = selector.querySelector('[data-qty-input]');
      if (!input) return;

      const min = parseInt(input.min, 10);
      const minVal = Number.isNaN(min) ? 1 : min;

      minus?.addEventListener('click', () => {
        const val = parseInt(input.value, 10) || minVal;
        if (val > minVal) input.value = val - 1;
      });

      plus?.addEventListener('click', () => {
        const val = parseInt(input.value, 10) || minVal;
        input.value = val + 1;
      });
    });
  }
}

/* ── Product Variants ── */
class ProductVariants {
  constructor() {
    this.form = document.querySelector('[data-product-form]');
    if (!this.form) return;

    const jsonEl = document.querySelector('[data-product-json]');
    if (!jsonEl) return;

    try {
      this.product = JSON.parse(jsonEl.textContent);
    } catch (e) {
      return;
    }

    this.select = this.form.querySelector('[data-variant-select]');
    this.groups = [...this.form.querySelectorAll('[data-variant-group]')];
    this.mainImage = document.querySelector('[data-product-main-image]');

    this.groups.forEach((group) => {
      group.querySelectorAll('[data-variant-option]').forEach((btn) => {
        btn.addEventListener('click', () => this.onOptionClick(group, btn));
      });
    });

    this.select?.addEventListener('change', () => this.onVariantChange());
    this.onVariantChange();
  }

  onOptionClick(group, btn) {
    group.querySelectorAll('[data-variant-option]').forEach((o) => {
      o.classList.remove('product-form__option--selected');
    });
    btn.classList.add('product-form__option--selected');
    this.updateVariantFromOptions();
  }

  updateVariantFromOptions() {
    const selected = this.groups.map((group) => {
      return group.querySelector('.product-form__option--selected')?.dataset.optionValue;
    });

    const variant = this.product.variants.find((v) => {
      return v.options.every((opt, i) => opt === selected[i]);
    });

    if (variant && this.select) {
      this.select.value = String(variant.id);
      this.onVariantChange();
    }
  }

  onVariantChange() {
    if (!this.select) return;

    const variant = this.product.variants.find((v) => v.id === Number(this.select.value));
    if (!variant) return;

    const priceEl = document.querySelector('[data-price-current]');
    const compareEl = document.querySelector('[data-price-compare]');
    const pmsPriceEl = document.querySelector('[data-pms-price-current]');
    const pmsCompareEl = document.querySelector('[data-pms-price-compare]');
    const stickyPrice = document.querySelector('[data-sticky-price]');
    const addBtn = document.querySelector('[data-add-to-cart]');
    const stickyBtn = document.querySelector('[data-sticky-add]');

    if (priceEl) priceEl.textContent = SlazyUtils.formatMoney(variant.price);

    if (compareEl) {
      if (variant.compare_at_price > variant.price) {
        compareEl.textContent = SlazyUtils.formatMoney(variant.compare_at_price);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }

    if (pmsPriceEl) {
      pmsPriceEl.textContent = SlazyUtils.formatMoney(variant.price);
      pmsPriceEl.dataset.variantPrice = String(variant.price);
    }

    if (pmsCompareEl) {
      if (variant.compare_at_price > variant.price) {
        pmsCompareEl.textContent = SlazyUtils.formatMoney(variant.compare_at_price);
        pmsCompareEl.hidden = false;
      } else {
        pmsCompareEl.hidden = true;
      }
    }

    if (stickyPrice) stickyPrice.textContent = SlazyUtils.formatMoney(variant.price);

    const label = variant.available ? 'Add to Cart' : 'Sold Out';
    if (addBtn) {
      addBtn.disabled = !variant.available;
      addBtn.textContent = label;
    }
    if (stickyBtn) {
      stickyBtn.disabled = !variant.available;
      stickyBtn.textContent = label;
    }

    if (variant.featured_image && this.mainImage) {
      const img = variant.featured_image.src || variant.featured_image;
      const imageId = variant.featured_image.id || variant.featured_image.image_id;
      const gallery = ProductPageGallery.getInstance();

      if (gallery) {
        if (imageId) {
          gallery.setIndexByImageId(imageId);
        } else if (img) {
          gallery.setIndexBySrc(typeof img === 'string' ? img : img.src);
        }
      } else {
        this.mainImage.style.opacity = '0';
        setTimeout(() => {
          this.mainImage.src = typeof img === 'string' ? img : img.src;
          this.mainImage.style.opacity = '1';
        }, 150);
      }
    }

    this.groups.forEach((group, index) => {
      const options = group.querySelectorAll('[data-variant-option]');
      options.forEach((btn) => {
        const value = btn.dataset.optionValue;
        const available = this.product.variants.some((v) => {
          return v.options[index] === value && v.available;
        });
        btn.classList.toggle('product-form__option--unavailable', !available);
        btn.disabled = !available;
      });
    });
  }
}

/* ── Sticky Add to Cart ── */
class StickyAddToCart {
  constructor() {
    this.bar = document.querySelector('[data-sticky-atc]');
    const trigger = document.querySelector('[data-add-to-cart]');
    const stickyBtn = document.querySelector('[data-sticky-add]');

    if (!this.bar || !trigger) return;

    stickyBtn?.addEventListener('click', () => trigger.click());

    const observer = new IntersectionObserver(
      ([entry]) => {
        this.bar.classList.toggle('is-visible', !entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '0px' }
    );

    observer.observe(trigger);
  }
}

/* ── Cart Drawer ── */
class CartDrawer {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    if (!this.drawer) return;

    this.itemsEl = this.drawer.querySelector('[data-cart-drawer-items]');
    this.emptyEl = this.drawer.querySelector('[data-cart-drawer-empty]');
    this.footerEl = this.drawer.querySelector('[data-cart-drawer-footer]');
    this.subtotalEl = this.drawer.querySelector('[data-cart-drawer-subtotal]');
    this.countEl = document.querySelector('[data-cart-count]');

    document.querySelectorAll('[data-cart-drawer-toggle]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    this.drawer.querySelectorAll('[data-cart-drawer-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });

    this.drawer.querySelector('.cart-drawer__overlay')?.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer.classList.contains('cart-drawer--open')) {
        this.close();
      }
    });

    window.SlazyCart = this;
  }

  open() {
    this.drawer.classList.add('cart-drawer--open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('is-locked');
    this.refresh();
  }

  close() {
    this.drawer.classList.remove('cart-drawer--open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('is-locked');
  }

  async refresh() {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) throw new Error('Cart fetch failed');
      const cart = await res.json();
      this.render(cart);
      this.updateCount(cart.item_count);
    } catch (e) {
      /* offline or preview mode */
    }
  }

  updateCount(count) {
    if (!this.countEl) return;
    this.countEl.textContent = count;
    this.countEl.hidden = count < 1;

    if (count > 0) {
      this.countEl.classList.remove('header__cart-count--pulse');
      void this.countEl.offsetWidth;
      this.countEl.classList.add('header__cart-count--pulse');
    }
  }

  render(cart) {
    const hasItems = cart.items && cart.items.length > 0;
    if (this.emptyEl) this.emptyEl.hidden = hasItems;
    if (this.itemsEl) this.itemsEl.hidden = !hasItems;
    if (this.footerEl) this.footerEl.hidden = !hasItems;

    if (!hasItems || !this.itemsEl) {
      if (this.itemsEl) this.itemsEl.innerHTML = '';
      return;
    }

    if (this.subtotalEl) {
      this.subtotalEl.textContent = SlazyUtils.formatMoney(cart.total_price);
    }

    this.itemsEl.innerHTML = cart.items.map((item) => {
      const title = SlazyUtils.escapeHtml(item.product_title);
      const variant = item.variant_title && item.variant_title !== 'Default Title'
        ? `<p class="cart-drawer__item-variant">${SlazyUtils.escapeHtml(item.variant_title)}</p>`
        : '';
      const image = item.image
        ? `<img src="${SlazyUtils.escapeHtml(item.image)}" alt="${title}" width="80" height="100" loading="lazy">`
        : '';

      return `
        <div class="cart-drawer__item">
          <a href="${SlazyUtils.escapeHtml(item.url)}" class="cart-drawer__item-image">${image}</a>
          <div class="cart-drawer__item-info">
            <a href="${SlazyUtils.escapeHtml(item.url)}" class="cart-drawer__item-title">${title}</a>
            ${variant}
            <p class="cart-drawer__item-price">${SlazyUtils.formatMoney(item.final_line_price)}</p>
          </div>
        </div>
      `;
    }).join('');
  }

  async addItem(variantId, quantity = 1) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.description || 'Could not add to cart');
      }

      await this.refresh();
      this.open();
      return true;
    } catch (e) {
      return false;
    }
  }
}

/* ── Ajax Cart (product form + quick add) ── */
class AjaxCart {
  constructor() {
    this.form = document.querySelector('[data-product-form]');
    if (this.form) {
      this.form.addEventListener('submit', (e) => this.onFormSubmit(e));
    }

    document.querySelectorAll('[data-quick-add]').forEach((btn) => {
      btn.addEventListener('click', (e) => this.onQuickAdd(e, btn));
    });
  }

  async onFormSubmit(e) {
    if (!window.SlazyCart) return;

    e.preventDefault();
    const formData = new FormData(this.form);
    const id = formData.get('id');
    const quantity = parseInt(formData.get('quantity'), 10) || 1;
    const btn = this.form.querySelector('[data-add-to-cart]');

    if (btn) {
      btn.classList.add('is-loading');
      btn.disabled = true;
    }

    const ok = await window.SlazyCart.addItem(id, quantity);

    if (btn) {
      btn.classList.remove('is-loading');
      btn.disabled = false;
      if (ok) {
        btn.classList.add('is-added');
        setTimeout(() => btn.classList.remove('is-added'), 1500);
      }
    }

    if (!ok) this.form.submit();
  }

  async onQuickAdd(e, btn) {
    e.preventDefault();
    e.stopPropagation();

    const variantId = btn.dataset.variantId;
    if (!variantId || !window.SlazyCart) return;

    btn.classList.add('is-loading');
    btn.disabled = true;

    const ok = await window.SlazyCart.addItem(variantId, 1);

    btn.classList.remove('is-loading');
    btn.disabled = false;

    if (ok) {
      btn.classList.add('is-added');
      const text = btn.querySelector('.product-card__quick-add-text');
      const done = btn.querySelector('.product-card__quick-add-done');
      if (text) text.hidden = true;
      if (done) done.hidden = false;
      setTimeout(() => {
        btn.classList.remove('is-added');
        if (text) text.hidden = false;
        if (done) done.hidden = true;
      }, 1800);
    }
  }
}

/* ── Product Carousel ── */
class ProductCarousel {
  constructor(element) {
    this.carousel = element;
    this.prevBtn = element.querySelector('[data-carousel-prev]');
    this.nextBtn = element.querySelector('[data-carousel-next]');
    this.offset = 0;
    this.autoplay = element.dataset.autoplay !== 'false';
    this.autoplayTimer = null;
    this.dragging = false;

    this.track = element.querySelector('[data-carousel-track]');
    if (!this.track) return;

    this.prevBtn?.addEventListener('click', () => this.scroll(-1));
    this.nextBtn?.addEventListener('click', () => this.scroll(1));

    this.onMove = this.onMove.bind(this);
    this.onEnd = this.onEnd.bind(this);

    this.track.addEventListener('mousedown', (e) => this.onStart(e.pageX));
    this.track.addEventListener('touchstart', (e) => this.onStart(e.touches[0].pageX), { passive: true });

    element.addEventListener('mouseenter', () => this.stopAutoplay());
    element.addEventListener('mouseleave', () => this.startAutoplay());

    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(() => {
        this.offset = Math.min(this.offset, this.getMaxOffset());
        this.updateTrack();
        this.updateButtons();
      }).observe(this.track.parentElement);
    } else {
      window.addEventListener('resize', () => {
        this.offset = Math.min(this.offset, this.getMaxOffset());
        this.updateTrack();
        this.updateButtons();
      });
    }

    this.initDrag();
    this.startAutoplay();
    this.updateButtons();
  }

  getMaxOffset() {
    if (!this.track?.parentElement) return 0;
    return Math.max(0, this.track.scrollWidth - this.track.parentElement.offsetWidth);
  }

  scroll(direction) {
    const card = this.track.querySelector('.product-card');
    if (!card) return;

    const gap = 12;
    const scrollAmount = card.offsetWidth + gap;
    const maxOffset = this.getMaxOffset();

    this.offset = Math.max(0, Math.min(this.offset + direction * scrollAmount, maxOffset));
    this.updateTrack();
    this.updateButtons();
  }

  updateTrack() {
    this.track.style.transform = `translate3d(-${this.offset}px, 0, 0)`;
  }

  updateButtons() {
    const maxOffset = this.getMaxOffset();
    if (this.prevBtn) this.prevBtn.disabled = this.offset <= 0;
    if (this.nextBtn) this.nextBtn.disabled = this.offset >= maxOffset - 1;
  }

  onStart(x) {
    this.dragging = true;
    this.startX = x;
    this.startOffset = this.offset;
    this.track.classList.add('is-dragging');
    this.stopAutoplay();

    window.addEventListener('mousemove', this.onMove);
    window.addEventListener('touchmove', this.onMove, { passive: true });
    window.addEventListener('mouseup', this.onEnd);
    window.addEventListener('touchend', this.onEnd);
  }

  onMove(e) {
    if (!this.dragging) return;
    const x = e.type === 'touchmove' ? e.touches[0].pageX : e.pageX;
    const diff = this.startX - x;
    const maxOffset = this.getMaxOffset();
    this.offset = Math.max(0, Math.min(this.startOffset + diff, maxOffset));
    this.updateTrack();
  }

  onEnd() {
    if (!this.dragging) return;
    this.dragging = false;
    this.track.classList.remove('is-dragging');
    this.updateButtons();
    this.startAutoplay();

    window.removeEventListener('mousemove', this.onMove);
    window.removeEventListener('touchmove', this.onMove);
    window.removeEventListener('mouseup', this.onEnd);
    window.removeEventListener('touchend', this.onEnd);
  }

  initDrag() {
    /* bound in onStart/onEnd */
  }

  startAutoplay() {
    if (!this.autoplay || SlazyUtils.prefersReducedMotion()) return;
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => {
      if (this.offset >= this.getMaxOffset() - 1) {
        this.offset = 0;
        this.updateTrack();
      } else {
        this.scroll(1);
      }
    }, 5000);
  }

  stopAutoplay() {
    if (this.autoplayTimer) clearInterval(this.autoplayTimer);
  }
}

/* ── Announcement Slider ── */
class AnnouncementSlider {
  constructor() {
    this.root = document.querySelector('[data-announcement-slider]');
    if (!this.root) return;

    this.track = this.root.querySelector('[data-announcement-track]');
    this.slides = this.track ? Array.from(this.track.children) : [];
    this.prev = this.root.querySelector('[data-announcement-prev]');
    this.next = this.root.querySelector('[data-announcement-next]');
    this.dots = this.root.querySelectorAll('[data-announcement-dot]');
    this.close = this.root.querySelector('[data-announcement-close]');
    this.index = 0;
    this.timer = null;
    this.speed = Number(this.root.dataset.autoplaySpeed) || 3000;

    this.close?.addEventListener('click', () => {
      this.root.classList.add('is-hidden');
      this.stop();
    });

    if (this.slides.length <= 1) return;

    this.prev?.addEventListener('click', () => this.go(this.index - 1));
    this.next?.addEventListener('click', () => this.go(this.index + 1));
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.go(Number(dot.dataset.announcementDot)));
    });

    this.start();
    this.root.addEventListener('mouseenter', () => this.stop());
    this.root.addEventListener('mouseleave', () => this.start());
  }

  go(nextIndex) {
    this.index = (nextIndex + this.slides.length) % this.slides.length;
    this.track.style.transform = `translateX(-${this.index * 100}%)`;
    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === this.index));
  }

  start() {
    if (SlazyUtils.prefersReducedMotion() || this.slides.length <= 1) return;
    this.stop();
    this.timer = setInterval(() => this.go(this.index + 1), this.speed);
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
  }
}

/* ── Scroll To Top ── */
class ScrollToTop {
  constructor() {
    this.btn = document.querySelector('[data-scroll-top]');
    if (!this.btn) return;

    window.addEventListener('scroll', () => {
      this.btn.classList.toggle('is-visible', window.scrollY > 400);
    }, { passive: true });

    this.btn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: SlazyUtils.prefersReducedMotion() ? 'auto' : 'smooth' });
    });
  }
}

/* ── Image Load Fade ── */
class ImageLoadFade {
  constructor() {
    document.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      if (img.complete) {
        img.classList.add('is-loaded');
      } else {
        img.addEventListener('load', () => img.classList.add('is-loaded'), { once: true });
        img.addEventListener('error', () => img.classList.add('is-loaded'), { once: true });
      }
    });
  }
}

/* ── Init ── */
function initSlazyFits() {
  new PageLoader();
  new MobileMenu();
  new StickyHeader();
  new AnnouncementSlider();
  new ScrollToTop();
  new ScrollReveal();
  new HeroMotion();
  new ParallaxBanners();
  new ProductPageGallery();
  new ProductMediaShowcase();
  new QuantitySelector();
  new ProductVariants();
  new StickyAddToCart();
  const cart = new CartDrawer();
  new AjaxCart();
  new ImageLoadFade();

  document.querySelectorAll('[data-info-rotator]').forEach((el) => new InfoBarRotator(el));
  document.querySelectorAll('[data-product-carousel]').forEach((el) => new ProductCarousel(el));

  document.addEventListener('slazy:loaded', () => {
    document.querySelectorAll('[data-animate], [data-animate-stagger]').forEach((el) => {
      if (el.getBoundingClientRect().top < window.innerHeight) {
        el.classList.add('is-visible');
      }
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSlazyFits);
} else {
  initSlazyFits();
}
